import { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Ban, Loader2, Search } from 'lucide-react';
import { BOOKING_BY_REFERENCE } from '../graphql/queries/booking';
import { CANCEL_BOOKING } from '../graphql/mutations/booking';

type Props = {
  agencyId: number;
  primaryColor: string;
};

function statutLabel(b: {
  status: string;
  statutDisponibilite?: string | null;
  statutReservation?: string | null;
}): string {
  if (b.status === 'CANCELLED') return 'Annulée';
  if (b.status === 'REJECTED' || b.statutReservation === 'refusée') return 'Refusée par l’agence';
  if (b.statutDisponibilite === 'disponible' || b.status === 'CONFIRMED') return 'Confirmée';
  if ((b.statutDisponibilite || 'attente') === 'attente') return 'En attente de validation';
  return b.statutReservation || b.status || '—';
}

export default function ClientReservationSection({ agencyId, primaryColor }: Props) {
  const [refId, setRefId] = useState('');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const [fetchBooking, { data, loading, called }] = useLazyQuery(BOOKING_BY_REFERENCE, {
    fetchPolicy: 'network-only',
  });

  const [cancelTrip, { loading: cancelling }] = useMutation(CANCEL_BOOKING);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const id = parseInt(refId.trim(), 10);
    if (!id || !email.trim()) {
      setErr('Indiquez le numéro de réservation et l’email utilisés lors de la demande.');
      return;
    }
    fetchBooking({ variables: { id, email: email.trim(), agencyId } });
  };

  const booking = data?.bookingByReference;
  const searched = called && !loading;
  const notFound = searched && booking == null;

  const handleCancelClient = async () => {
    if (!booking) return;
    if (!window.confirm('Confirmer l’annulation de cette réservation ?')) return;
    setErr(null);
    try {
      await cancelTrip({
        variables: {
          id: booking.id,
          annulationPar: 'client',
          clientEmail: email.trim(),
          agencyId,
        },
      });
      await fetchBooking({
        variables: {
          id: booking.id,
          email: email.trim(),
          agencyId,
        },
      });
    } catch (e: unknown) {
      const apollo = e as { graphQLErrors?: { message: string }[]; message?: string };
      const msg =
        apollo.graphQLErrors?.[0]?.message ||
        apollo.message ||
        "Impossible d'annuler pour le moment.";
      setErr(msg);
    }
  };

  const canCancel = Boolean(
    booking && booking.status !== 'CANCELLED' && booking.status !== 'REJECTED'
  );

  return (
    <section id="ma-reservation" className="scroll-mt-24 py-20 bg-white border-t border-gray-200">
      <div className="container mx-auto px-4 max-w-2xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Ma réservation</h2>
          <p className="text-gray-600 text-sm">
            Consultez le statut de votre demande ou annulez-la avec le numéro de référence reçu après envoi et
            l’email saisi sur le formulaire.
          </p>
        </div>

        <form onSubmit={handleSearch} className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50/80 p-6 shadow-sm">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">N° de réservation</label>
            <input
              type="text"
              inputMode="numeric"
              value={refId}
              onChange={(e) => setRefId(e.target.value.replace(/\D/g, ''))}
              placeholder="ex. 42"
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="le même que sur la demande"
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm"
              autoComplete="email"
            />
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: primaryColor }}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Recherche…
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Afficher ma réservation
              </>
            )}
          </button>
        </form>

        {notFound && (
          <p className="mt-6 text-center text-sm text-gray-500">
            Aucune réservation ne correspond à ces informations. Vérifiez le numéro et l’email.
          </p>
        )}

        {booking && (
          <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
              <p className="text-sm text-gray-500">
                Réf. <span className="font-mono font-semibold text-gray-900">#{booking.id}</span>
              </p>
              <span
                className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-800"
              >
                {statutLabel(booking)}
              </span>
            </div>
            <div className="text-sm space-y-1 text-gray-700">
              <p>
                <span className="text-gray-500">Séjour :</span> {booking.package?.title || '—'}
              </p>
              <p>
                <span className="text-gray-500">Formule :</span> {booking.formula?.name || '—'}
              </p>
              <p>
                <span className="text-gray-500">Montant estimé :</span>{' '}
                {booking.totalPrice != null ? `${Number(booking.totalPrice).toLocaleString('fr-DZ')} DA` : '—'}
              </p>
            </div>

            {booking.status === 'CANCELLED' && (
              <p className="text-sm text-slate-700 rounded-lg bg-slate-50 px-3 py-2">
                Cette réservation a été annulée
                {booking.annulationPar === 'client'
                  ? ' (à votre demande)'
                  : booking.annulationPar === 'agence'
                    ? ' par l’agence'
                    : ''}
                .
              </p>
            )}

            {canCancel && (
              <button
                type="button"
                disabled={cancelling}
                onClick={handleCancelClient}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800 hover:bg-red-100 disabled:opacity-60"
              >
                {cancelling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Ban className="h-4 w-4" />
                )}
                Annuler cette réservation
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
